// Data

import { ReactElement } from 'react'
import { APIData, APIListData } from '../../NonReact/common/Types/Types'

export type EstateData = {
    ChildCMEnvironmentsCount: number
    TelSyssCount: number
    PartitionsCount: number
    DevicesCount: number
    ACDAgentsCount: number
    ReportingUsersCount: number
    NonReportingUsersCount: number
}

export type ServicePackItem = {
    Value: string
    Enabled: boolean
    TelSysIDs: string
    ItemType: string
    SendWelcomeEmail: boolean
}

export type ServicePackItemForm = {
    Value: string
    Enabled: boolean
    TelSysIDs: number[]
    ItemType: string
    SendWelcomeEmail: boolean
}

export type BroadworksProvisioning = {
    ServicePacks?: ServicePackItem[]
}

export type ProvisioningConfig = {
    BroadworksProvisioning?: BroadworksProvisioning
}

export type LicenseManagementValues = {
    EssentialsCap: Nullable<number>
    ProfessionalCap: Nullable<number>
    EnterpriseCap: Nullable<number>
    CallCentreAgentCap: Nullable<number>
    CallCentreSupervisorCap: Nullable<number>
    CXIntegrationCap: Nullable<number>
}

export type CMEDetailsData = {
    FromAddressName: string
    Capabilities: number
    CompanyName: string
    AppDownloadURL: string
    CXIClientDownloadURLWin?: string
    CXIClientDownloadURLMac?: string
    SearchAndCallAppDownloadURL?: string
    UseAuthentication: boolean
    IdentityProviderIDs: string
    Description: string
    DisableAkixiLogin: boolean
    FromAddressEmail: string
    ServerIPAddress: string
    CommunicationActivated: boolean
    ServerTCPPort: string
    CMEnvironmentID: string
    Domain: string
    ParentCMEnvironmentID: string
    BillingTag: string
    AuthUsername: string
    AuthPassword: string
    ProvisioningConfig?: ProvisioningConfig
    LicenseCapManagement: LicenseManagementValues
    BotsEnabledTelsys?: number
    BotsDisabledTelsys?: number
    MaxTelsysCount: number
    BrandingConfig?: Record<
        string,
        Record<string, string | Record<string, string>> | CMEBrandingRepeatableEntry[] | boolean
    >
    hasPreviousBrandingConfig?: boolean
    hasPreviewBrandingConfig?: boolean
    PreviewBrandingGUID?: string
    BrandingGUID?: string
    CXIAccessCode: string
    ShowUiVersionToggle: boolean
}

export type CMEBrandingRepeatableEntry = Record<string, string | File | Record<string, string>>

export type CMEBranding = Record<
    string,
    Record<string, string | File | Record<string, string>> | CMEBrandingRepeatableEntry[] | boolean
>

export type CMEBrandingEntry = [
    string,
    Record<string, string | File | Record<string, string>> | CMEBrandingRepeatableEntry[] | boolean
]

export type CMEBrandingSubItemEntry = [string, string | File | Record<string, string>]

export type CMEBrandingSubItemBackendEntry = [string, string | Record<string, string>]

export type CMEBrandingBackendWrapper = {
    BrandingConfig: CMEBrandingBackend
    BrandingGUID: string
}

export type CMEBrandingBackend = Record<
    string,
    Record<string, string | Record<string, string>> | CMEBrandingRepeatableEntry[] | boolean
>

export type CMEBrandingBackendEntry = [
    string,
    Record<string, string | Record<string, string>> | CMEBrandingRepeatableEntry[] | boolean
]

export type CMEFormHookData = {
    details: {
        description: string
        id: string
        billingid: string
        parentcme: string
        providerids: string
        disableAkixiLogin: boolean
    }
    domainProfile: {
        domain: string
        serveripaddress: string
        tcpport: number
        fromaddressemail: string
        fromaddressname: string
        companyname: string
        teamsappdownloadurl: string
        cxiClientWindowsUrl: string
        cxiClientMacUrl: string
        searchAndCallUrl: string
        communicationactive: boolean
        useauthentication: boolean
        username: string
        password: string
        cxiAccessCode: string
    }
    provisioning: {
        broadworks: {
            servicepacks: ServicePackItemForm[]
        }
    }
    capabilities: {
        maxTelsysCount: string
        cxaBroadworks: boolean
        cxaWebex: boolean
        cxaTeams: boolean
        cxiBroadworks: boolean
        cxiWebex: boolean
        cxiTeams: boolean
        legacyTelephony: boolean
        legacy: boolean
        enterprise: boolean
        showUiVersionToggle: boolean
    }
    licenseManagement: {
        EssentialsCap: string
        ProfessionalCap: string
        EnterpriseCap: string
        CallCentreAgentCap: string
        CallCentreSupervisorCap: string
        CXIntegrationCap: string
    }
    advanced?: {
        recursiveBotEnable: boolean
    }
    branding?: CMEBranding
}

export type CMEParentItem = {
    Description?: string
    CMEnvironmentID: string
}

export type CMEParentData = APIListData<CMEParentItem>

export type CMELicenseCapInputRequirements = {
    cmeID: string
}

export type RealTimeBotsStatus = {
    disabledTelSysCount?: number
    enabledTelSysCount?: number
}

export type CapabilitiesEnabled = {
    cxaBroadworks: boolean
    cxaWebex: boolean
    cxaTeams: boolean
    cxiBroadworks: boolean
    cxiWebex: boolean
    cxiTeams: boolean
    legacyTelephony: boolean
    legacy: boolean
    enterprise: boolean
}

export enum BrandingFieldTypes {
    FILE = 'FILE',
    TEXT = 'TEXT',
    COLOUR = 'COLOUR',
    LIST = 'LIST',
    LINK = 'LINK'
}

export type LightDark = { light: string; dark: string }

export enum BackgroundMode {
    DARK = 'dark',
    LIGHT = 'light',
    PRIMARY = 'primary'
}

export type UpdateTo =
    | {
          name: string
          shouldContrast?: boolean
          lightenOrDarken?: undefined
      }
    | {
          name: string
          shouldContrast?: undefined
          lightenOrDarken: number
      }

export type FileUploadDimensions = {
    max: {
        width: number
        height: number
    }
    min: {
        width: number
        height: number
    }
}

export type Validations = {
    accepts?: string[]
    maxFileSize?: number
    dimensions?: FileUploadDimensions
    pattern?: string
    required?: boolean
    maxLength?: number
    maxWordLength?: number
}

export interface BrandingElement {
    type: 'COLOUR' | 'FILE' | 'TEXT' | 'LIST' | 'LINK'
    fieldName: string
    label: string
    helpID?: string
    defaultValue?: string
    hasDarkMode?: boolean
    rootOnly?: boolean
    updateTo?: UpdateTo[]
    validations?: Validations
    subEntries?: BrandingElement[]
}

export interface ColourBranding extends BrandingElement {
    type: 'COLOUR'
    fieldName: string
    label: string
    helpID?: string
    defaultValue?: string
    hasDarkMode?: boolean
    updateTo?: UpdateTo[]
    rootOnly?: boolean
    validations?: Validations
    subEntries?: undefined
}

export interface FileBranding extends BrandingElement {
    type: 'FILE'
    fieldName: string
    fileTypeFieldName: string
    label: string
    helpID?: string
    rootOnly?: boolean
    defaultValue?: undefined
    hasDarkMode?: undefined
    updateTo?: undefined
    validations?: Validations
    subEntries?: undefined
    bgMode?: BackgroundMode
}

export interface TextBranding extends BrandingElement {
    type: 'TEXT'
    fieldName: string
    label: string
    helpID?: string
    rootOnly?: boolean
    defaultValue?: undefined
    hasDarkMode?: undefined
    updateTo?: undefined
    validations?: Validations
    subEntries?: undefined
}

export interface LinkBranding extends BrandingElement {
    type: 'LINK'
    fieldName: string
    label: string
    helpID?: string
    rootOnly?: boolean
    defaultValue?: undefined
    hasDarkMode?: undefined
    updateTo?: undefined
    validations?: Validations
    subEntries?: undefined
}

export type BaseBranding = ColourBranding | FileBranding | TextBranding | LinkBranding

export interface ListBranding extends BrandingElement {
    type: 'LIST'
    fieldName: string
    label: string
    helpID?: string
    subEntries: BaseBranding[]
    rootOnly?: boolean
    defaultValue?: undefined
    hasDarkMode?: undefined
    updateTo?: undefined
    validations?: Validations
}

export type BrandingData = BaseBranding | ListBranding

export type BrandingArea<T extends unknown> = {
    id?: string
    rootOnly?: boolean
    title: string
    entries: T[]
}

export type BrandingConfig = BrandingArea<BrandingData>

export type BrandingConfigs = BrandingArea<BrandingConfig>[]

export type BrandingTab = {
    id: string
    label: string
    isError?: boolean
    component: (isTabActive: boolean) => ReactElement
}

export type CMEAddChangeResponse = APIData<{ BrandingGUID?: string }>
